import { ActionPayloadValidationError, requireMinorAmountField, requireStringField } from "@slot/client-engine";
import type { ReplayContext, RoundAction, RoundActionHandler } from "@slot/client-engine";
import { GRID_CELL_COUNT } from "../gridSize";
import { POTS, POT_CAPACITY, parseSymbolId, type PotKind, type PotLevels } from "../rules/symbols";

/**
 * This title's actions on the wire (`.claude/docs/game-design.md`, "The round on the wire"), each parsed and validated
 * here before anything plays: a payload of the wrong shape is a `ProtocolError` (the shell's reload), because every one
 * of them is the server's word about money or the board. Indices and counts are digit strings, money is minor units.
 * The server authors every one never skippable: each changes what the board, the pots or the meter show.
 */
export const ACTIONS = {
  updatePots: "update_pots",
  presentBonusTrigger: "present_bonus_trigger",
  activateBoosters: "activate_boosters",
  refillRespins: "refill_respins",
  collectCoins: "collect_coins",
  dropMultipliers: "drop_multipliers",
  revealMysteries: "reveal_mysteries",
  awardFullBoard: "award_full_board",
  collectBoard: "collect_board",
} as const;

export interface UpdatePots {
  /** The line's bet these levels hold at (decision 56): a round at another starts with empty pots, another line count keeps them. */
  betMinor: string;
  levelsBefore: PotLevels;
  /** The gems the spin's coins carry into the pots, in landing order. */
  marks: readonly { index: number; pot: PotKind }[];
  levelsAfter: PotLevels;
}

export interface BonusTrigger {
  reason: "line" | "pot" | "buy";
  /** The cells that start it: the covered lines, or the gems that filled a pot (none for a buy). */
  cells: readonly number[];
}

export interface Boosters {
  life: boolean;
  multi: boolean;
  grow: boolean;
  multiEveryRespin: boolean;
  /** What the bonus's values and jackpots are multiples of (decision 56): a line's bet, or a bought bonus's whole bet. */
  valueBetMinor: string;
}

export interface CollectCoins {
  collectorIndex: number;
  sources: readonly { index: number; amountMinor: string }[];
  /** The collect's value after it collected (the sum of the sources). */
  valueMinor: string;
}

export interface DropMultipliers {
  /** Each hit cell's multiplier after the hit, in throw order (a cell may repeat). */
  cells: readonly { index: number; multiplier: number }[];
}

export interface RevealMysteries {
  cells: readonly { index: number; symbolId: string }[];
}

export interface AwardFullBoard {
  jackpot: "grand" | "royal";
  amountMinor: string;
}

export interface CollectBoard {
  /** Each filled cell's pay (its value times its multiplier), in collection order. */
  cells: readonly { index: number; amountMinor: string }[];
  /** The round's win: the cells and the full board's jackpot, capped. */
  totalMinor: string;
}

const invalid = (type: string, field: string, message: string): ActionPayloadValidationError => new ActionPayloadValidationError(type, field, message);

function record(type: string, value: unknown, field: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw invalid(type, field, "Must be an object.");
  return value as Record<string, unknown>;
}

function list(type: string, payload: Record<string, unknown>, field: string): Record<string, unknown>[] {
  const value = payload[field];
  if (!Array.isArray(value)) throw invalid(type, field, "Must be an array.");
  return value.map((entry, i) => record(type, entry, `${field}[${i}]`));
}

function cell(type: string, payload: Record<string, unknown>, field: string): number {
  const raw = requireStringField(type, payload, field);
  if (!/^\d+$/.test(raw) || Number(raw) >= GRID_CELL_COUNT) throw invalid(type, field, `Must be a cell index 0–${GRID_CELL_COUNT - 1}, got "${raw}".`);
  return Number(raw);
}

function flag(type: string, payload: Record<string, unknown>, field: string): boolean {
  const value = payload[field];
  if (typeof value !== "boolean") throw invalid(type, field, "Must be a boolean.");
  return value;
}

function pot(type: string, payload: Record<string, unknown>, field: string): PotKind {
  const value = requireStringField(type, payload, field);
  if (!(POTS as readonly string[]).includes(value)) throw invalid(type, field, `Must be one of ${POTS.join(", ")}, got "${value}".`);
  return value as PotKind;
}

function levels(type: string, payload: Record<string, unknown>, field: string): PotLevels {
  const raw = record(type, payload[field], field);
  const out: Record<PotKind, number> = { life: 0, multi: 0, grow: 0 };
  for (const kind of POTS) {
    const value = requireStringField(type, raw, kind);
    if (!/^\d$/.test(value) || Number(value) > POT_CAPACITY) throw invalid(type, `${field}.${kind}`, `Must be a gem count 0–${POT_CAPACITY}, got "${value}".`);
    out[kind] = Number(value);
  }
  return out;
}

export function parseUpdatePots(type: string, payload: Record<string, unknown>): UpdatePots {
  return {
    betMinor: requireMinorAmountField(type, payload, "betMinor"),
    levelsBefore: levels(type, payload, "levelsBefore"),
    marks: list(type, payload, "marks").map((m, i) => ({ index: cell(`${type}.marks[${i}]`, m, "positionIndex"), pot: pot(`${type}.marks[${i}]`, m, "pot") })),
    levelsAfter: levels(type, payload, "levelsAfter"),
  };
}

export function parseBonusTrigger(type: string, payload: Record<string, unknown>): BonusTrigger {
  const reason = requireStringField(type, payload, "reason");
  if (reason !== "line" && reason !== "pot" && reason !== "buy") throw invalid(type, "reason", `Must be line, pot or buy, got "${reason}".`);
  const mask = requireStringField(type, payload, "positionMask");
  if (mask.length !== GRID_CELL_COUNT || !/^[01]+$/.test(mask)) throw invalid(type, "positionMask", `Must be a ${GRID_CELL_COUNT}-cell "0/1" mask.`);
  return { reason, cells: [...mask].flatMap((bit, i) => (bit === "1" ? [i] : [])) };
}

export function parseBoosters(type: string, payload: Record<string, unknown>): Boosters {
  return {
    life: flag(type, payload, "life"),
    multi: flag(type, payload, "multi"),
    grow: flag(type, payload, "grow"),
    multiEveryRespin: flag(type, payload, "multiEveryRespin"),
    valueBetMinor: requireMinorAmountField(type, payload, "valueBetMinor"),
  };
}

export function parseCollectCoins(type: string, payload: Record<string, unknown>): CollectCoins {
  const sources = list(type, payload, "sources").map((s, i) => ({ index: cell(`${type}.sources[${i}]`, s, "positionIndex"), amountMinor: requireMinorAmountField(`${type}.sources[${i}]`, s, "amountMinor") }));
  const valueMinor = requireMinorAmountField(type, payload, "valueMinor");
  const sum = sources.reduce((total, s) => total + BigInt(s.amountMinor), 0n);
  if (sum.toString() !== valueMinor) throw invalid(type, "valueMinor", `Must be the sources' sum (${sum}), got "${valueMinor}".`);
  return { collectorIndex: cell(type, payload, "collectorIndex"), sources, valueMinor };
}

export function parseDropMultipliers(type: string, payload: Record<string, unknown>): DropMultipliers {
  return {
    cells: list(type, payload, "cells").map((c, i) => {
      const multiplier = requireStringField(`${type}.cells[${i}]`, c, "multiplier");
      if (!/^[1-9]\d*$/.test(multiplier) || Number(multiplier) < 2) throw invalid(type, `cells[${i}].multiplier`, `Must be a whole number of at least 2, got "${multiplier}".`);
      return { index: cell(`${type}.cells[${i}]`, c, "positionIndex"), multiplier: Number(multiplier) };
    }),
  };
}

export function parseRevealMysteries(type: string, payload: Record<string, unknown>): RevealMysteries {
  return {
    cells: list(type, payload, "cells").map((c, i) => {
      const symbolId = requireStringField(`${type}.cells[${i}]`, c, "symbolId");
      const symbol = parseSymbolId(symbolId);
      if (symbol?.kind !== "coin" && symbol?.kind !== "jackpot") throw invalid(type, `cells[${i}].symbolId`, `Must be a coin or a jackpot coin, got "${symbolId}".`);
      return { index: cell(`${type}.cells[${i}]`, c, "positionIndex"), symbolId };
    }),
  };
}

export function parseAwardFullBoard(type: string, payload: Record<string, unknown>): AwardFullBoard {
  const jackpot = requireStringField(type, payload, "jackpot");
  if (jackpot !== "grand" && jackpot !== "royal") throw invalid(type, "jackpot", `Must be grand or royal, got "${jackpot}".`);
  return { jackpot, amountMinor: requireMinorAmountField(type, payload, "amountMinor") };
}

export function parseCollectBoard(type: string, payload: Record<string, unknown>): CollectBoard {
  return {
    cells: list(type, payload, "cells").map((c, i) => ({ index: cell(`${type}.cells[${i}]`, c, "positionIndex"), amountMinor: requireMinorAmountField(`${type}.cells[${i}]`, c, "amountMinor") })),
    totalMinor: requireMinorAmountField(type, payload, "totalMinor"),
  };
}

/** An action handler: the payload parsed (a malformed one throws before anything plays), then `run`. */
export function handler<T>(actionTypeName: string, parse: (type: string, payload: Record<string, unknown>) => T, run: (input: T, context: ReplayContext) => Promise<void>): RoundActionHandler {
  return {
    actionTypeName,
    handleAction: (action: RoundAction, context: ReplayContext) => run(parse(actionTypeName, action.actionPayload), context),
  };
}
